import React from "react";

import {
  bindSingleAssetAgentView, createSingleAssetAgentConversation,
  getSingleAssetAgentConversation, getSingleAssetBindingView,
} from "../../api/client";
import { accountingViewFromBinding } from "./SingleAssetAgentPanels";
import { bindingViewRecoveryMessage } from "./singleAssetViewState.js";
import { historyMessage } from "./agentMessageFormat";

/**
 * 会话生命周期：新建会话、切换到某条历史会话、退出登录。
 *
 * 【当前没有界面入口 —— 2026-08-31，按产品要求删掉了左上角「会话管理」下拉】
 *
 * 删的是入口，不是能力：会话数据、后端接口、这里的逻辑都完好，想加回来只要在某处
 * 渲染一个入口，把下面返回的东西接上去：
 *
 *   requestNewSession()          打开「新建会话」确认框（newSessionDialogOpen）
 *   startNewSession()            确认后真正新建，并把当前右侧目标挂到新会话上
 *   openConversation(id)         打开一条历史会话（需要先有一个列会话的界面，
 *                                  接口是 GET /api/single-asset-agent/conversations）
 *   requestLogout()              有待确认操作时先拦一下，否则调用 onLogout
 *
 * 历史入口长什么样见 git：`SessionManagement.jsx`（下拉菜单 + 历史会话弹窗）在
 * 2026-08-31 之前由 `SingleAssetAgentPanels.jsx` 的标题栏渲染。
 * 背景与恢复步骤：docs/sdd/astock-agent-v07-single-asset-agent-behavior/parked-session-management.md
 *
 * 抽成 hook 的直接原因是 SingleAssetAgentDev.jsx 贴着 900 行上限
 * （`tests/test_frontend_contracts.py::test_frontend_files_stay_below_size_ceiling`），
 * 和旁边的 `useSingleAssetMarketView.js` 同一个理由。
 */
export function useSingleAssetSession({
  activeBinding,
  activeConversationKey,
  activeSlotRef,
  calculationResult,
  directAccountingView,
  displayAssetCode,
  hasPendingAgentDecision,
  busy,
  fileImporting,
  input,
  marketView,
  onLogout,
  onSessionRouteChange,
  setActiveBinding,
  setConversationId,
  setDirectAccountingView,
  setDirectMarketView,
  setInput,
  setMessages,
  setRequestError,
  setRetainedWorkspace,
  setRevision,
  setRows,
  setTurnResult,
}) {
  const [sessionBusy, setSessionBusy] = React.useState(false);
  const [newSessionDialogOpen, setNewSessionDialogOpen] = React.useState(false);
  // 不收 interactionBusy：那个值由调用方用本 hook 返回的 sessionBusy 算出来，
  // 收进来就成环了。这里用组成它的两个原始信号自己拼。
  const interactionBusy = busy || fileImporting || sessionBusy;

  async function startNewSession() {
    if (interactionBusy) return;
    setSessionBusy(true);
    setRequestError("");
    try {
      const fresh = await createSingleAssetAgentConversation();
      window.sessionStorage.setItem(activeConversationKey, fresh.conversation_id);
      let mountedBinding = null;
      let mountedView = null;
      if (activeBinding?.view_type === "holding" && activeBinding.target_id) {
        mountedBinding = await bindSingleAssetAgentView(
          fresh.conversation_id, fresh.revision,
          { type: "holding", target_id: activeBinding.target_id, asset_code: activeBinding.asset_code },
        );
      } else if (activeBinding?.view_type === "workspace" && activeBinding.target_id) {
        mountedBinding = await bindSingleAssetAgentView(
          fresh.conversation_id, fresh.revision,
          {
            type: "workspace",
            asset_code: activeBinding.asset_code,
            source_workspace_id: activeBinding.target_id,
            source_workspace_revision: directAccountingView?.target?.revision,
          },
        );
      } else if (activeBinding?.view_type === "market" && activeBinding.asset_code) {
        mountedBinding = await bindSingleAssetAgentView(
          fresh.conversation_id, fresh.revision,
          { type: "market", asset_code: activeBinding.asset_code },
        );
      }
      if (mountedBinding) {
        mountedView = await getSingleAssetBindingView(
          fresh.conversation_id, mountedBinding.binding_id,
        );
      }
      const mountedConversation = mountedBinding
        ? {
            ...fresh,
            revision: mountedBinding.conversation_revision,
            current_binding_id: mountedBinding.binding_id,
          }
        : fresh;
      setConversationId(fresh.conversation_id);
      setRevision(mountedConversation.revision);
      setRows([]);
      setMessages([]);
      setRetainedWorkspace(
        marketView
          ? { marketView }
          : calculationResult
            ? { calculationResult }
            : null,
      );
      setTurnResult(mountedConversation);
      setActiveBinding(mountedBinding);
      activeSlotRef.current = {
        conversationId: fresh.conversation_id,
        bindingId: mountedBinding?.binding_id ?? null,
      };
      setDirectMarketView(mountedView?.market_view ?? marketView);
      setDirectAccountingView(accountingViewFromBinding(mountedView));
      setRequestError(bindingViewRecoveryMessage(mountedView));
      setInput("");
      setNewSessionDialogOpen(false);
      onSessionRouteChange?.({
        conversationId: fresh.conversation_id,
        bindingId: mountedBinding?.binding_id ?? null,
        viewMode: mountedBinding?.view_type ?? (marketView ? "market" : null),
        assetCode: mountedBinding?.asset_code ?? (displayAssetCode || null),
        targetId: mountedBinding?.target_id ?? null,
      });
    } catch (error) {
      setRequestError(error.message);
    } finally {
      setSessionBusy(false);
    }
  }

  async function openConversation(conversationToOpen) {
    if (interactionBusy) return;
    if (input.trim()) {
      throw new Error("请先发送或清空当前输入框，再切换会话。");
    }
    setSessionBusy(true);
    setRequestError("");
    try {
      const latest = await getSingleAssetAgentConversation(
        conversationToOpen,
        { historyLimit: 200 },
      );
      window.sessionStorage.setItem(activeConversationKey, latest.conversation_id);
      setConversationId(latest.conversation_id);
      setRevision(latest.revision);
      setRows(latest.rows ?? []);
      setTurnResult(latest);
      let restoredView = null;
      if (latest.current_binding_id) {
        restoredView = await getSingleAssetBindingView(
          latest.conversation_id, latest.current_binding_id,
        );
      }
      setActiveBinding(restoredView?.binding ?? null);
      activeSlotRef.current = {
        conversationId: latest.conversation_id,
        bindingId: restoredView?.binding?.binding_id ?? null,
      };
      setDirectMarketView(restoredView?.market_view ?? null);
      setDirectAccountingView(accountingViewFromBinding(restoredView));
      setRequestError(bindingViewRecoveryMessage(restoredView));
      setRetainedWorkspace(null);
      setInput("");
      setMessages((latest.history ?? []).map(historyMessage));
      onSessionRouteChange?.({
        conversationId: latest.conversation_id,
        bindingId: restoredView?.binding?.binding_id ?? null,
        viewMode: restoredView?.binding?.view_type ?? null,
        assetCode: restoredView?.binding?.asset_code ?? null,
        targetId: restoredView?.binding?.target_id ?? null,
      });
    } catch (error) {
      setRequestError(error.message);
      throw error;
    } finally {
      setSessionBusy(false);
    }
  }

  function requestLogout() {
    if (hasPendingAgentDecision) {
      setRequestError("当前有待确认操作。请先在 Agent 中确认、取消或补充说明，再退出。");
      return;
    }
    onLogout?.();
  }

  return {
    sessionBusy,
    newSessionDialogOpen,
    openConversation,
    requestLogout,
    requestNewSession: () => setNewSessionDialogOpen(true),
    closeNewSessionDialog: () => setNewSessionDialogOpen(false),
    startNewSession,
  };
}
